import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import { EntityServices } from "@sdk/utils/entity.service";
import type { iHolidayQuoteX } from "@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { UserTracker } from "user-tracker";
import type {
  iCreateQuoteFromTemplateRequest,
  iCreateQuoteFromTemplateResponse,
  iCreateTemplateFromQuoteRequest,
  iQuoteTemplate,
} from "./quote-templates-model";

export class QuoteTemplatesServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }

  constructor(protected config: { basePath: string; token: string }) {
    this.quoteTemplates.configure(config);
  }

  quoteTemplates = Object.assign(
    new EntityServices<iQuoteTemplate>(SDKConfig, "quote-templates", {
      onCreate: () => {
        UserTracker.track("QUOTE_TEMPLATES - Create", {});
      },
      onDelete: () => {
        UserTracker.track("QUOTE_TEMPLATES - Delete", {});
      },
    }),
    {
      createTemplateFromQuote: async (req: iCreateTemplateFromQuoteRequest) => {
        UserTracker.track("QUOTE_TEMPLATES - Create From Quote");
        const results: AxiosResponse<iQuoteTemplate> = await axios.post(
          this.config.basePath + `/quote-templates/from-quote`,
          req,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },

      previewTemplate: async (id: string, startDate?: string) => {
        const params = startDate
          ? `?startDate=${encodeURIComponent(startDate)}`
          : "";
        const results: AxiosResponse<iHolidayQuoteX> = await axios.get(
          this.config.basePath + `/quote-templates/${id}/preview${params}`,
          {
            headers: {
              Authorization: this.config.token,
            },
          }
        );
        return results.data;
      },

      createQuoteFromTemplate: async (
        id: string,
        req: iCreateQuoteFromTemplateRequest
      ) => {
        UserTracker.track("QUOTE_TEMPLATES - Create Quote From Template");
        const results: AxiosResponse<iCreateQuoteFromTemplateResponse> =
          await axios.post(
            this.config.basePath + `/quote-templates/${id}/create-quote`,
            req,
            {
              headers: {
                Authorization: this.config.token,
              },
            }
          );
        return results.data;
      },
    }
  );

  /** Aliases matching the plan method names (delegate to EntityServices). */
  createTemplateFromQuote = (req: iCreateTemplateFromQuoteRequest) =>
    this.quoteTemplates.createTemplateFromQuote(req);

  queryTemplates = (req: { query: any; options: any }) =>
    this.quoteTemplates.query(req);

  getTemplate = (id: string) => this.quoteTemplates.getById(id);

  patchTemplate = (
    id: string,
    edits: Partial<Pick<iQuoteTemplate, "name" | "description">>
  ) => this.quoteTemplates.editById(id, edits);

  deleteTemplate = (id: string) => this.quoteTemplates.deleteById(id);

  previewTemplate = (id: string, startDate?: string) =>
    this.quoteTemplates.previewTemplate(id, startDate);

  createQuoteFromTemplate = (
    id: string,
    req: iCreateQuoteFromTemplateRequest
  ) => this.quoteTemplates.createQuoteFromTemplate(id, req);
}
